import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useRestrictionsGet, Restriction } from '../../../clients/graphql/pages/restrictions-queries';

export const RestrictionsPage = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useRestrictionsGet({});
  const restrictions = data?.restrictionsGet?.restrictions || [];

  const handleRowClick = (params: any) => {
    navigate(`/developer/restrictions/${params.row.restrictionKey}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching restrictions. Please reload the page.
        </Alert>
      </Box>
    );
  }

  const columns = [
    {
      field: 'restrictionKey',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 200,
    },
    {
      field: 'value',
      headerName: 'Value',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number;
        return (
          <Typography variant="body2">
            {value !== null && value !== undefined ? value : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'pollFrequency',
      headerName: 'Poll Frequency',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? `${params.value}s` : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'stripeProductId',
      headerName: 'Stripe Product',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' 
        }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Enabled' : 'Disabled'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
  ];

  const rows = restrictions.map((restriction: Restriction, index: number) => ({
    id: index,
    ...restriction,
  }));

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
        Restrictions
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={45}
          onRowClick={handleRowClick}
          sx={{
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
          disableColumnMenu
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};